import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const HodInternshipAttendance = ({ onNavigate, userInfo }) => {

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Attendance data
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  // Filter states
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('today');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  // Action states
  const [actionLoading, setActionLoading] = useState(false);
  const [actionSuccess, setActionSuccess] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [selectedRecords, setSelectedRecords] = useState(new Set());

  // Internship assignment states
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [newInternship, setNewInternship] = useState({
    studentId: '',
    companyName: '',
    position: '',
    startDate: '',
    endDate: '',
    companyAddress: '',
    mentorName: '',
    mentorEmail: '',
    mentorPhone: '',
    stipend: '',
    workingHours: '',
    description: ''
  });

  // Statistics
  const [statistics, setStatistics] = useState({
    totalSubmissions: 0,
    pendingReviews: 0,
    approvedToday: 0,
    rejectedToday: 0,
    totalStudents: 0,
    totalCompanies: 0
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);





  // Fetch real data from API - Updated to use actual backend endpoints
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('🔍 Starting API call to fetch internship attendance data...');

        const token = localStorage.getItem('token') || sessionStorage.getItem('token');

        if (!token) {
          setError('Authentication token not found. Please login again.');
          return;
        }



        // Fetch internship attendance data - NEW ENDPOINT
        console.log('📊 Fetching internship attendance data...');
        const attendanceResponse = await fetch(`${API_LINK}/api/hod/internship-attendance`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!attendanceResponse.ok) {
          throw new Error(`HTTP error! status: ${attendanceResponse.status}`);
        }

        const attendanceResult = await attendanceResponse.json();

        if (attendanceResult.success) {
          console.log('✅ Internship attendance data loaded successfully');
          console.log(`📈 Found ${attendanceResult.data.records.length} attendance records`);

          setAttendanceRecords(attendanceResult.data.records);
          setFilteredRecords(attendanceResult.data.records);
          setStatistics(attendanceResult.data.statistics);
        } else {
          throw new Error(attendanceResult.message || 'Failed to fetch attendance data');
        }

        // Fetch students for internship assignment - NEW ENDPOINT
        console.log('👥 Fetching students for internship assignment...');
        const studentsResponse = await fetch(`${API_LINK}/api/hod/internship-students`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (studentsResponse.ok) {
          const studentsResult = await studentsResponse.json();
          if (studentsResult.success) {
            console.log(`👨‍🎓 Found ${studentsResult.data.length} students`);
            setStudents(studentsResult.data);
          }
        }

      } catch (err) {
        console.error('💥 Error loading internship attendance data:', err);
        setError(`Failed to load data: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [userInfo]);

  // Filter and sort records
  useEffect(() => {
    let filtered = [...attendanceRecords];

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(record => record.status === statusFilter);
    }

    // Apply date filter
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const thisWeek = new Date(today);
    thisWeek.setDate(thisWeek.getDate() - 7);

    if (dateFilter === 'today') {
      filtered = filtered.filter(record => {
        const recordDate = new Date(record.check_in_time);
        return recordDate >= today;
      });
    } else if (dateFilter === 'yesterday') {
      filtered = filtered.filter(record => {
        const recordDate = new Date(record.check_in_time);
        return recordDate >= yesterday && recordDate < today;
      });
    } else if (dateFilter === 'week') {
      filtered = filtered.filter(record => {
        const recordDate = new Date(record.check_in_time);
        return recordDate >= thisWeek;
      });
    }

    // Apply company filter
    if (companyFilter !== 'all') {
      filtered = filtered.filter(record => record.company_name === companyFilter);
    }

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(record =>
        record.student_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.student_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.position.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.location_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.check_in_time) - new Date(a.check_in_time);
        case 'oldest':
          return new Date(a.check_in_time) - new Date(b.check_in_time);
        case 'student':
          return a.student_name.localeCompare(b.student_name);
        case 'company':
          return a.company_name.localeCompare(b.company_name);
        case 'status':
          return a.status.localeCompare(b.status);
        default:
          return 0;
      }
    });

    setFilteredRecords(filtered);
    setCurrentPage(1);
  }, [attendanceRecords, statusFilter, searchTerm, dateFilter, companyFilter, sortBy]);


  // Handle attendance action (approve/reject) - Updated to use real API
  const handleAttendanceAction = async (recordId, action, notes = '', reviewerName = 'HOD') => {
    try {
      setActionLoading(true);
      setActionError(null);
      setActionSuccess(null);

      console.log(`🔄 Processing ${action} for record ${recordId}`);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      const response = await fetch(`${API_LINK}/api/hod/internship-attendance/${recordId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: action,
          notes: notes
        })
      });

      const result = await response.json();

      if (result.success) {
        console.log(`✅ Successfully ${action} record ${recordId}`);

        // Update local state
        setAttendanceRecords(prev => prev.map(record =>
          record.id === recordId ? {
            ...record,
            status: action,
            review_notes: notes,
            reviewed_by: reviewerName,
            reviewed_at: new Date().toISOString()
          } : record
        ));

        if (selectedRecord && selectedRecord.id === recordId) {
          setSelectedRecord({
            ...selectedRecord,
            status: action,
            review_notes: notes,
            reviewed_by: reviewerName,
            reviewed_at: new Date().toISOString()
          });
        }

        setActionSuccess(result.message);
        setReviewNotes('');

        setTimeout(() => setActionSuccess(null), 3000);
      } else {
        setActionError(result.message);
      }

    } catch (error) {
      console.error('💥 Error updating attendance:', error);
      setActionError(`Failed to perform action: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle internship assignment - Updated to use real API
  const handleAssignInternship = async () => {
    try {
      setAssignLoading(true);
      setActionError(null);
      setActionSuccess(null);

      console.log('🚀 Assigning new internship...');

      // Validation
      if (!newInternship.studentId || !newInternship.companyName || !newInternship.position ||
        !newInternship.startDate || !newInternship.endDate) {
        setActionError('Please fill in all required fields');
        setAssignLoading(false);
        return;
      }

      if (new Date(newInternship.startDate) >= new Date(newInternship.endDate)) {
        setActionError('End date must be after start date');
        setAssignLoading(false);
        return;
      }

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      const response = await fetch(`${API_LINK}/api/hod/internship-assign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newInternship)
      });

      const result = await response.json();

      if (result.success) {
        console.log('✅ Internship assigned successfully');
        setActionSuccess(result.message);

        // Reset form
        setNewInternship({
          studentId: '',
          companyName: '',
          position: '',
          startDate: '',
          endDate: '',
          companyAddress: '',
          mentorName: '',
          mentorEmail: '',
          mentorPhone: '',
          stipend: '',
          workingHours: '',
          description: ''
        });

        // Update student status
        setStudents(prev => prev.map(student =>
          student.id === newInternship.studentId
            ? { ...student, hasActiveInternship: true }
            : student
        ));

        setShowAssignModal(false);

        setTimeout(() => setActionSuccess(null), 5000);
      } else {
        setActionError(result.message);
      }

    } catch (error) {
      console.error('💥 Error assigning internship:', error);
      setActionError(`Failed to assign internship: ${error.message}`);
    } finally {
      setAssignLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setNewInternship(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Get available students (those without active internships)
  const getAvailableStudents = () => {
    return students.filter(student => !student.hasActiveInternship);
  };

  // Handle bulk actions
  const handleBulkAction = async (action, reviewerName = 'HOD') => {
    if (selectedRecords.size === 0) {
      setActionError('Please select records to perform bulk action');
      return;
    }

    const confirmMessage = `Are you sure you want to ${action} ${selectedRecords.size} selected records?`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    console.log(`🔄 Performing bulk ${action} on ${selectedRecords.size} records`);

    for (const recordId of selectedRecords) {
      await handleAttendanceAction(recordId, action, '', reviewerName);
    }

    setSelectedRecords(new Set());
  };

  // Toggle record selection
  const toggleRecordSelection = (recordId) => {
    const newSelection = new Set(selectedRecords);
    if (newSelection.has(recordId)) {
      newSelection.delete(recordId);
    } else {
      newSelection.add(recordId);
    }
    setSelectedRecords(newSelection);
  };

  // Select/deselect all records
  const toggleAllSelection = () => {
    if (selectedRecords.size === currentRecords.length) {
      setSelectedRecords(new Set());
    } else {
      setSelectedRecords(new Set(currentRecords.map(record => record.id)));
    }
  };



  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Format time
  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status info
  const getStatusInfo = (status) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return { color: '#ffc107', bg: '#fff3cd', icon: '⏳', text: 'Pending Review' };
      case 'approved':
        return { color: '#28a745', bg: '#d4edda', icon: '✅', text: 'Approved' };
      case 'rejected':
        return { color: '#dc3545', bg: '#f8d7da', icon: '❌', text: 'Rejected' };
      default:
        return { color: '#6c757d', bg: '#e2e3e5', icon: '❓', text: status };
    }
  };

  // Get unique companies for filter
  const getUniqueCompanies = () => {
    const companies = [...new Set(attendanceRecords.map(record => record.company_name))];
    return companies.sort();
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentRecords = filteredRecords.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredRecords.length / itemsPerPage);

  return (
    <Layout role="hod" onNavigate={onNavigate} userInfo={userInfo} activePage="hod-internship">
      {({ hodData, isMobile, handleLogout }) => (
        <>
          {/* Modal CSS */}
          {/* Loading Component */}
          {loading && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 'calc(100vh - 200px)',
              flexDirection: 'column',
              gap: '1rem'
            }}>
              <div style={{
                width: '50px',
                height: '50px',
                border: '4px solid #e3f2fd',
                borderTop: '4px solid #238548',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading Internship Attendance...</p>
            </div>
          )}

          {/* Error Component */}
          {error && !loading && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 'calc(100vh - 200px)',
              flexDirection: 'column',
              gap: '1rem',
              padding: '2rem'
            }}>
              <div style={{
                backgroundColor: '#ffffff',
                padding: '3rem',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                textAlign: 'center',
                maxWidth: '400px',
                border: '1px solid #e0e0e0'
              }}>
                <div style={{ fontSize: '4rem', color: '#ff6b6b', marginBottom: '1.5rem' }}>⚠️</div>
                <h3 style={{ color: '#333', margin: '0 0 1rem 0', fontSize: '1.4rem', fontWeight: 600 }}>Unable to Load Internship Attendance</h3>
                <p style={{ color: '#666', margin: '0 0 2rem 0', fontSize: '1rem', lineHeight: 1.5 }}>{error}</p>
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <button onClick={() => window.location.reload()} style={{ backgroundColor: '#238548', color: 'white', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '8px', cursor: 'pointer', fontSize: '1rem', fontWeight: 500 }}>Try Again</button>
                  <button onClick={handleLogout} style={{ backgroundColor: '#666', color: 'white', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '8px', cursor: 'pointer', fontSize: '1rem', fontWeight: 500 }}>Logout</button>
                </div>
              </div>
            </div>
          )}

          {/* Main Content */}
          {!loading && !error && (
            <>
              <style>
                {`
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 2000;
            padding: 1rem;
            box-sizing: border-box;
          }
          
          .modal-content {
            background: white;
            border-radius: 12px;
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
            max-width: 900px;
            width: 100%;
            max-height: 90vh;
            overflow-y: auto;
            position: relative;
          }
          
          .photo-modal {
            max-width: 80vw;
            max-height: 80vh;
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 2rem;
          }
          
          .photo-modal img {
            max-width: 100%;
            max-height: 70vh;
            object-fit: contain;
            border-radius: 8px;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
          }
        `}
              </style>
              {/* Page Header */}
              <div style={{
                background: '#ffffff',
                padding: '2rem',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                marginBottom: '2rem',
                borderLeft: '4px solid #238548'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '1rem'
                }}>
                  <div>
                    <h2 style={{
                      color: '#238548',
                      margin: '0 0 0.5rem 0',
                      fontSize: '1.8rem',
                      fontWeight: 'bold'
                    }}>
                      🎓 Internship Attendance Management
                    </h2>
                    <p style={{
                      color: '#666',
                      margin: 0,
                      fontSize: '1rem'
                    }}>
                      Review and manage student internship attendance submissions for {userInfo?.department || 'your department'}
                    </p>
                  </div>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    flexWrap: 'wrap'
                  }}>
                    <button
                      onClick={() => setShowAssignModal(true)}
                      style={{
                        background: '#17a2b8',
                        color: '#ffffff',
                        border: 'none',
                        padding: '0.75rem 1.5rem',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: '500',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#138496';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#17a2b8';
                      }}
                    >
                      ➕ Assign Internship
                    </button>

                    <div style={{
                      background: '#f8f9fa',
                      padding: '0.75rem 1rem',
                      borderRadius: '8px',
                      border: '1px solid #e9ecef'
                    }}>
                      <strong>Total: {statistics.totalSubmissions}</strong>
                    </div>

                    <div style={{
                      background: '#fff3cd',
                      padding: '0.75rem 1rem',
                      borderRadius: '8px',
                      border: '1px solid #ffeaa7'
                    }}>
                      Pending: {statistics.pendingReviews}
                    </div>
                  </div>
                </div>
              </div>

              {/* Statistics Cards */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1.5rem',
                marginBottom: '2rem'
              }}>
                {[
                  {
                    icon: '📋',
                    title: 'Total Submissions',
                    value: statistics.totalSubmissions,
                    subtitle: 'All time submissions',
                    color: '#238548'
                  },
                  {
                    icon: '⏳',
                    title: 'Pending Reviews',
                    value: statistics.pendingReviews,
                    subtitle: 'Awaiting your review',
                    color: '#ffc107'
                  },
                  {
                    icon: '✅',
                    title: 'Approved Today',
                    value: statistics.approvedToday,
                    subtitle: 'Approved submissions',
                    color: '#28a745'
                  },
                  {
                    icon: '👥',
                    title: 'Active Students',
                    value: statistics.totalStudents,
                    subtitle: 'Students with internships',
                    color: '#17a2b8'
                  }
                ].map((stat, index) => (
                  <div key={index} style={{
                    background: '#ffffff',
                    padding: '1.5rem',
                    borderRadius: '12px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
                    }}>
                    <div style={{
                      fontSize: '2rem',
                      color: stat.color,
                      marginBottom: '0.5rem'
                    }}>
                      {stat.icon}
                    </div>
                    <h3 style={{ margin: '0 0 0.25rem 0', color: '#333', fontSize: '0.9rem' }}>{stat.title}</h3>
                    <p style={{
                      fontSize: '1.8rem',
                      fontWeight: 'bold',
                      color: stat.color,
                      margin: '0.25rem 0'
                    }}>
                      {stat.value}
                    </p>
                    <p style={{
                      color: '#666',
                      margin: 0,
                      fontSize: '0.8rem'
                    }}>
                      {stat.subtitle}
                    </p>
                  </div>
                ))}
              </div>

              {/* Action Messages */}
              {(actionSuccess || actionError) && (
                <div style={{
                  background: actionSuccess ? '#d4edda' : '#f8d7da',
                  border: `1px solid ${actionSuccess ? '#c3e6cb' : '#f5c6cb'}`,
                  color: actionSuccess ? '#155724' : '#721c24',
                  padding: '1rem',
                  borderRadius: '8px',
                  marginBottom: '2rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{ fontSize: '1.2rem' }}>
                    {actionSuccess ? '✅' : '❌'}
                  </span>
                  {actionSuccess || actionError}
                </div>
              )}

              {/* Filters and Search */}
              <div style={{
                background: '#ffffff',
                padding: '1.5rem',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                marginBottom: '2rem'
              }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '1rem',
                  alignItems: 'end'
                }}>
                  {/* Search */}
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '500',
                      color: '#333'
                    }}>
                      🔍 Search
                    </label>
                    <input
                      type="text"
                      placeholder="Search by student, company, or location..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e9ecef',
                        borderRadius: '8px',
                        fontSize: '0.95rem',
                        transition: 'border-color 0.3s ease'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#238548';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e9ecef';
                      }}
                    />
                  </div>

                  {/* Status Filter */}
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '500',
                      color: '#333'
                    }}>
                      Status Filter
                    </label>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e9ecef',
                        borderRadius: '8px',
                        fontSize: '0.95rem',
                        backgroundColor: '#fff'
                      }}
                    >
                      <option value="all">All Status</option>
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>

                  {/* Date Filter */}
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '500',
                      color: '#333'
                    }}>
                      Date Filter
                    </label>
                    <select
                      value={dateFilter}
                      onChange={(e) => setDateFilter(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e9ecef',
                        borderRadius: '8px',
                        fontSize: '0.95rem',
                        backgroundColor: '#fff'
                      }}
                    >
                      <option value="all">All Dates</option>
                      <option value="today">Today</option>
                      <option value="yesterday">Yesterday</option>
                      <option value="week">This Week</option>
                    </select>
                  </div>

                  {/* Company Filter */}
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '500',
                      color: '#333'
                    }}>
                      Company Filter
                    </label>
                    <select
                      value={companyFilter}
                      onChange={(e) => setCompanyFilter(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e9ecef',
                        borderRadius: '8px',
                        fontSize: '0.95rem',
                        backgroundColor: '#fff'
                      }}
                    >
                      <option value="all">All Companies</option>
                      {getUniqueCompanies().map(company => (
                        <option key={company} value={company}>{company}</option>
                      ))}
                    </select>
                  </div>

                  {/* Sort By */}
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '500',
                      color: '#333'
                    }}>
                      Sort By
                    </label>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e9ecef',
                        borderRadius: '8px',
                        fontSize: '0.95rem',
                        backgroundColor: '#fff'
                      }}
                    >
                      <option value="newest">Newest First</option>
                      <option value="oldest">Oldest First</option>
                      <option value="student">Student Name</option>
                      <option value="company">Company Name</option>
                      <option value="status">Status</option>
                    </select>
                  </div>

                  {/* Clear Filters */}
                  <div>
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        setStatusFilter('all');
                        setDateFilter('today');
                        setCompanyFilter('all');
                        setSortBy('newest');
                      }}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: '#f8f9fa',
                        border: '2px solid #e9ecef',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '0.95rem',
                        fontWeight: '500',
                        color: '#333',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#e9ecef';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = '#f8f9fa';
                      }}
                    >
                      🗑️ Clear Filters
                    </button>
                  </div>
                </div>
              </div>

              {/* Bulk Actions */}
              {selectedRecords.size > 0 && (
                <div style={{
                  background: '#e3f2fd',
                  padding: '1rem',
                  borderRadius: '8px',
                  marginBottom: '2rem',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  border: '1px solid #bbdefb'
                }}>
                  <span style={{ fontWeight: '500', color: '#1976d2' }}>
                    {selectedRecords.size} record(s) selected
                  </span>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={() => handleBulkAction('approved', hodData?.name)}
                      disabled={actionLoading}
                      style={{
                        padding: '0.5rem 1rem',
                        background: '#28a745',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: '500'
                      }}
                    >
                      ✅ Approve All
                    </button>
                    <button
                      onClick={() => handleBulkAction('rejected', hodData?.name)}
                      disabled={actionLoading}
                      style={{
                        padding: '0.5rem 1rem',
                        background: '#dc3545',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: '500'
                      }}
                    >
                      ❌ Reject All
                    </button>
                  </div>
                </div>
              )}

              {/* Attendance Records List */}
              <div style={{
                background: '#ffffff',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                overflow: 'hidden',
                marginBottom: '2rem'
              }}>
                {/* Table Header */}
                <div style={{
                  background: '#f8f9fa',
                  padding: '1rem',
                  borderBottom: '1px solid #e9ecef',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem'
                }}>
                  <input
                    type="checkbox"
                    checked={selectedRecords.size === currentRecords.length && currentRecords.length > 0}
                    onChange={toggleAllSelection}
                    style={{
                      width: '16px',
                      height: '16px',
                      cursor: 'pointer'
                    }}
                  />
                  <span style={{ fontWeight: '500', color: '#333' }}>
                    Select All ({currentRecords.length} records)
                  </span>
                </div>

                {currentRecords.length > 0 ? (
                  currentRecords.map((record, index) => {
                    const statusInfo = getStatusInfo(record.status);

                    return (
                      <div
                        key={record.id}
                        style={{
                          padding: '1.5rem',
                          borderBottom: index < currentRecords.length - 1 ? '1px solid #ecf0f1' : 'none',
                          transition: 'all 0.3s ease',
                          backgroundColor: '#ffffff'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f8f9fa';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#ffffff';
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          gap: '1rem',
                          alignItems: 'start'
                        }}>
                          {/* Checkbox */}
                          <input
                            type="checkbox"
                            checked={selectedRecords.has(record.id)}
                            onChange={() => toggleRecordSelection(record.id)}
                            style={{
                              width: '16px',
                              height: '16px',
                              cursor: 'pointer',
                              marginTop: '0.25rem'
                            }}
                          />

                          {/* Record Content */}
                          <div style={{ flex: 1 }}>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'start',
                              marginBottom: '1rem',
                              flexWrap: 'wrap',
                              gap: '1rem'
                            }}>
                              <div style={{ flex: 1, minWidth: '300px' }}>
                                <h3 style={{
                                  margin: '0 0 0.5rem 0',
                                  fontSize: '1.1rem',
                                  fontWeight: 'bold',
                                  color: '#333'
                                }}>
                                  {record.student_name} ({record.student_id})
                                </h3>
                                <p style={{
                                  margin: '0 0 0.5rem 0',
                                  color: '#666',
                                  fontSize: '0.9rem'
                                }}>
                                  <strong>{record.company_name}</strong> • {record.position}
                                </p>
                                <p style={{
                                  margin: '0 0 0.5rem 0',
                                  color: '#777',
                                  fontSize: '0.85rem'
                                }}>
                                  📍 {record.location_name}
                                </p>
                                <p style={{
                                  margin: 0,
                                  color: '#777',
                                  fontSize: '0.85rem'
                                }}>
                                  📧 {record.student_email}
                                </p>
                              </div>

                              <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-end',
                                gap: '0.5rem',
                                minWidth: '150px'
                              }}>
                                <span style={{
                                  padding: '0.25rem 0.75rem',
                                  borderRadius: '15px',
                                  fontSize: '0.75rem',
                                  fontWeight: 'bold',
                                  backgroundColor: statusInfo.bg,
                                  color: statusInfo.color,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.25rem'
                                }}>
                                  {statusInfo.icon} {statusInfo.text}
                                </span>

                                <div style={{
                                  fontSize: '0.8rem',
                                  color: '#999',
                                  textAlign: 'right'
                                }}>
                                  <div>🕒 {formatTime(record.check_in_time)}</div>
                                  <div>📅 {formatDate(record.check_in_time)}</div>
                                </div>
                              </div>
                            </div>

                            {/* Review Notes */}
                            {record.review_notes && (
                              <div style={{
                                background: '#f8f9fa',
                                padding: '0.75rem',
                                borderRadius: '6px',
                                marginBottom: '1rem',
                                border: '1px solid #e9ecef'
                              }}>
                                <div style={{
                                  fontSize: '0.8rem',
                                  color: '#666',
                                  marginBottom: '0.25rem'
                                }}>
                                  Review by {record.reviewed_by} • {formatDate(record.reviewed_at)}
                                </div>
                                <div style={{ fontSize: '0.9rem', color: '#333' }}>
                                  {record.review_notes}
                                </div>
                              </div>
                            )}

                            {/* Action Buttons */}
                            <div style={{
                              display: 'flex',
                              gap: '0.5rem',
                              alignItems: 'center',
                              flexWrap: 'wrap'
                            }}>
                              <button
                                onClick={() => {
                                  setSelectedRecord(record);
                                  setShowDetailModal(true);
                                  setActionError(null);
                                  setActionSuccess(null);
                                }}
                                style={{
                                  padding: '0.5rem 1rem',
                                  background: '#17a2b8',
                                  color: '#ffffff',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '0.85rem',
                                  fontWeight: '500'
                                }}
                              >
                                👁️ View Details
                              </button>

                              <button
                                onClick={() => {
                                  setSelectedPhoto(`${API_LINK}${record.photo_url}`);
                                  setShowPhotoModal(true);
                                }}
                                style={{
                                  padding: '0.5rem 1rem',
                                  background: '#6f42c1',
                                  color: '#ffffff',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '0.85rem',
                                  fontWeight: '500'
                                }}
                              >
                                📷 View Photo
                              </button>

                              {record.status === 'pending' && (
                                <>
                                  <button
                                    onClick={() => handleAttendanceAction(record.id, 'approved')}
                                    disabled={actionLoading}
                                    style={{
                                      padding: '0.5rem 1rem',
                                      background: '#28a745',
                                      color: '#ffffff',
                                      border: 'none',
                                      borderRadius: '6px',
                                      cursor: 'pointer',
                                      fontSize: '0.85rem',
                                      fontWeight: '500',
                                      opacity: actionLoading ? 0.7 : 1
                                    }}
                                  >
                                    ✅ Approve
                                  </button>

                                  <button
                                    onClick={() => handleAttendanceAction(record.id, 'rejected')}
                                    disabled={actionLoading}
                                    style={{
                                      padding: '0.5rem 1rem',
                                      background: '#dc3545',
                                      color: '#ffffff',
                                      border: 'none',
                                      borderRadius: '6px',
                                      cursor: 'pointer',
                                      fontSize: '0.85rem',
                                      fontWeight: '500',
                                      opacity: actionLoading ? 0.7 : 1
                                    }}
                                  >
                                    ❌ Reject
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div style={{
                    padding: '3rem',
                    textAlign: 'center',
                    color: '#666'
                  }}>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</div>
                    <h3 style={{ margin: '0 0 1rem 0' }}>No Attendance Records Found</h3>
                    <p style={{ margin: 0 }}>
                      {searchTerm || statusFilter !== 'all' || dateFilter !== 'today' || companyFilter !== 'all'
                        ? 'Try adjusting your search filters'
                        : 'No attendance submissions have been made yet'
                      }
                    </p>
                  </div>
                )}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '1rem',
                  marginBottom: '2rem'
                }}>
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '2px solid #238548',
                      borderRadius: '8px',
                      background: currentPage === 1 ? '#f8f9fa' : '#238548',
                      color: currentPage === 1 ? '#6c757d' : '#ffffff',
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    ← Previous
                  </button>

                  <span style={{
                    padding: '0.5rem 1rem',
                    fontSize: '0.9rem',
                    color: '#666'
                  }}>
                    Page {currentPage} of {totalPages}
                  </span>

                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '2px solid #238548',
                      borderRadius: '8px',
                      background: currentPage === totalPages ? '#f8f9fa' : '#238548',
                      color: currentPage === totalPages ? '#6c757d' : '#ffffff',
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    Next →
                  </button>
                </div>
              )}


              {/* Assign Internship Modal */}
              {showAssignModal && (
                <div className="modal-overlay" onClick={() => setShowAssignModal(false)}>
                  <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                    {/* Modal Header */}
                    <div style={{
                      background: '#17a2b8',
                      color: '#ffffff',
                      padding: '1.5rem 2rem',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <h2 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 'bold' }}>
                        ➕ Assign New Internship
                      </h2>
                      <button
                        onClick={() => setShowAssignModal(false)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#ffffff',
                          fontSize: '1.5rem',
                          cursor: 'pointer',
                          padding: '0.25rem',
                          borderRadius: '4px',
                          lineHeight: 1
                        }}
                      >
                        ✕
                      </button>
                    </div>

                    {/* Modal Content */}
                    <div style={{ padding: '2rem', maxHeight: '70vh', overflowY: 'auto' }}>
                      <form onSubmit={(e) => { e.preventDefault(); handleAssignInternship(); }}>
                        {/* Student Selection */}
                        <div style={{
                          background: '#f8f9fa',
                          padding: '1.5rem',
                          borderRadius: '8px',
                          marginBottom: '1.5rem',
                          border: '1px solid #e9ecef'
                        }}>
                          <h3 style={{ margin: '0 0 1rem 0', color: '#333' }}>Student Selection</h3>
                          <div style={{ marginBottom: '1rem' }}>
                            <label style={{
                              display: 'block',
                              marginBottom: '0.5rem',
                              fontWeight: '500',
                              color: '#333'
                            }}>
                              Select Student <span style={{ color: '#dc3545' }}>*</span>
                            </label>
                            <select
                              value={newInternship.studentId}
                              onChange={(e) => handleInputChange('studentId', e.target.value)}
                              required
                              style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '2px solid #e9ecef',
                                borderRadius: '8px',
                                fontSize: '0.95rem',
                                backgroundColor: '#fff'
                              }}
                            >
                              <option value="">Choose a student...</option>
                              {getAvailableStudents().map(student => (
                                <option key={student.id} value={student.id}>
                                  {student.name} ({student.id}) - {student.year}
                                </option>
                              ))}
                            </select>
                            {getAvailableStudents().length === 0 && (
                              <p style={{ color: '#dc3545', fontSize: '0.9rem', margin: '0.5rem 0 0 0' }}>
                                No students available for internship assignment
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Company Details */}
                        <div style={{
                          background: '#f8f9fa',
                          padding: '1.5rem',
                          borderRadius: '8px',
                          marginBottom: '1.5rem',
                          border: '1px solid #e9ecef'
                        }}>
                          <h3 style={{ margin: '0 0 1rem 0', color: '#333' }}>Company Details</h3>
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                            gap: '1rem'
                          }}>
                            <div>
                              <label style={{
                                display: 'block',
                                marginBottom: '0.5rem',
                                fontWeight: '500',
                                color: '#333'
                              }}>
                                Company Name <span style={{ color: '#dc3545' }}>*</span>
                              </label>
                              <input
                                type="text"
                                value={newInternship.companyName}
                                onChange={(e) => handleInputChange('companyName', e.target.value)}
                                required
                                placeholder="Enter company name"
                                style={{
                                  width: '100%',
                                  padding: '0.75rem',
                                  border: '2px solid #e9ecef',
                                  borderRadius: '8px',
                                  fontSize: '0.95rem'
                                }}
                              />
                            </div>
                            <div>
                              <label style={{
                                display: 'block',
                                marginBottom: '0.5rem',
                                fontWeight: '500',
                                color: '#333'
                              }}>
                                Position <span style={{ color: '#dc3545' }}>*</span>
                              </label>
                              <input
                                type="text"
                                value={newInternship.position}
                                onChange={(e) => handleInputChange('position', e.target.value)}
                                required
                                placeholder="e.g., Software Developer Intern"
                                style={{
                                  width: '100%',
                                  padding: '0.75rem',
                                  border: '2px solid #e9ecef',
                                  borderRadius: '8px',
                                  fontSize: '0.95rem'
                                }}
                              />
                            </div>
                          </div>
                          <div style={{ marginTop: '1rem' }}>
                            <label style={{
                              display: 'block',
                              marginBottom: '0.5rem',
                              fontWeight: '500',
                              color: '#333'
                            }}>
                              Company Address
                            </label>
                            <textarea
                              value={newInternship.companyAddress}
                              onChange={(e) => handleInputChange('companyAddress', e.target.value)}
                              placeholder="Enter complete company address"
                              rows={2}
                              style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '2px solid #e9ecef',
                                borderRadius: '8px',
                                fontSize: '0.95rem',
                                resize: 'vertical'
                              }}
                            />
                          </div>
                        </div>

                        {/* Internship Duration */}
                        <div style={{
                          background: '#f8f9fa',
                          padding: '1.5rem',
                          borderRadius: '8px',
                          marginBottom: '1.5rem',
                          border: '1px solid #e9ecef'
                        }}>
                          <h3 style={{ margin: '0 0 1rem 0', color: '#333' }}>Internship Duration</h3>
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
                            gap: '1rem'
                          }}>
                            <div>
                              <label style={{
                                display: 'block',
                                marginBottom: '0.5rem',
                                fontWeight: '500',
                                color: '#333'
                              }}>
                                Start Date <span style={{ color: '#dc3545' }}>*</span>
                              </label>
                              <input
                                type="date"
                                value={newInternship.startDate}
                                onChange={(e) => handleInputChange('startDate', e.target.value)}
                                required
                                min={new Date().toISOString().split('T')[0]}
                                style={{
                                  width: '100%',
                                  padding: '0.75rem',
                                  border: '2px solid #e9ecef',
                                  borderRadius: '8px',
                                  fontSize: '0.95rem'
                                }}
                              />
                            </div>
                            <div>
                              <label style={{
                                display: 'block',
                                marginBottom: '0.5rem',
                                fontWeight: '500',
                                color: '#333'
                              }}>
                                End Date <span style={{ color: '#dc3545' }}>*</span>
                              </label>
                              <input
                                type="date"
                                value={newInternship.endDate}
                                onChange={(e) => handleInputChange('endDate', e.target.value)}
                                required
                                min={newInternship.startDate || new Date().toISOString().split('T')[0]}
                                style={{
                                  width: '100%',
                                  padding: '0.75rem',
                                  border: '2px solid #e9ecef',
                                  borderRadius: '8px',
                                  fontSize: '0.95rem'
                                }}
                              />
                            </div>
                            <div>
                              <label style={{
                                display: 'block',
                                marginBottom: '0.5rem',
                                fontWeight: '500',
                                color: '#333'
                              }}>
                                Working Hours
                              </label>
                              <input
                                type="text"
                                value={newInternship.workingHours}
                                onChange={(e) => handleInputChange('workingHours', e.target.value)}
                                placeholder="e.g., 9 AM - 5 PM"
                                style={{
                                  width: '100%',
                                  padding: '0.75rem',
                                  border: '2px solid #e9ecef',
                                  borderRadius: '8px',
                                  fontSize: '0.95rem'
                                }}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Mentor Details */}
                        <div style={{
                          background: '#f8f9fa',
                          padding: '1.5rem',
                          borderRadius: '8px',
                          marginBottom: '1.5rem',
                          border: '1px solid #e9ecef'
                        }}>
                          <h3 style={{ margin: '0 0 1rem 0', color: '#333' }}>Mentor Details</h3>
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
                            gap: '1rem'
                          }}>
                            <div>
                              <label style={{
                                display: 'block',
                                marginBottom: '0.5rem',
                                fontWeight: '500',
                                color: '#333'
                              }}>
                                Mentor Name
                              </label>
                              <input
                                type="text"
                                value={newInternship.mentorName}
                                onChange={(e) => handleInputChange('mentorName', e.target.value)}
                                placeholder="Enter mentor name"
                                style={{
                                  width: '100%',
                                  padding: '0.75rem',
                                  border: '2px solid #e9ecef',
                                  borderRadius: '8px',
                                  fontSize: '0.95rem'
                                }}
                              />
                            </div>
                            <div>
                              <label style={{
                                display: 'block',
                                marginBottom: '0.5rem',
                                fontWeight: '500',
                                color: '#333'
                              }}>
                                Mentor Email
                              </label>
                              <input
                                type="email"
                                value={newInternship.mentorEmail}
                                onChange={(e) => handleInputChange('mentorEmail', e.target.value)}
                                placeholder="mentor@company.com"
                                style={{
                                  width: '100%',
                                  padding: '0.75rem',
                                  border: '2px solid #e9ecef',
                                  borderRadius: '8px',
                                  fontSize: '0.95rem'
                                }}
                              />
                            </div>
                            <div>
                              <label style={{
                                display: 'block',
                                marginBottom: '0.5rem',
                                fontWeight: '500',
                                color: '#333'
                              }}>
                                Mentor Phone
                              </label>
                              <input
                                type="tel"
                                value={newInternship.mentorPhone}
                                onChange={(e) => handleInputChange('mentorPhone', e.target.value)}
                                placeholder="+91 98765 43210"
                                style={{
                                  width: '100%',
                                  padding: '0.75rem',
                                  border: '2px solid #e9ecef',
                                  borderRadius: '8px',
                                  fontSize: '0.95rem'
                                }}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Additional Details */}
                        <div style={{
                          background: '#f8f9fa',
                          padding: '1.5rem',
                          borderRadius: '8px',
                          marginBottom: '1.5rem',
                          border: '1px solid #e9ecef'
                        }}>
                          <h3 style={{ margin: '0 0 1rem 0', color: '#333' }}>Additional Details</h3>
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                            gap: '1rem',
                            marginBottom: '1rem'
                          }}>
                            <div>
                              <label style={{
                                display: 'block',
                                marginBottom: '0.5rem',
                                fontWeight: '500',
                                color: '#333'
                              }}>
                                Stipend (₹)
                              </label>
                              <input
                                type="number"
                                value={newInternship.stipend}
                                onChange={(e) => handleInputChange('stipend', e.target.value)}
                                placeholder="e.g., 15000"
                                min="0"
                                style={{
                                  width: '100%',
                                  padding: '0.75rem',
                                  border: '2px solid #e9ecef',
                                  borderRadius: '8px',
                                  fontSize: '0.95rem'
                                }}
                              />
                            </div>
                            <div></div>
                          </div>
                          <div>
                            <label style={{
                              display: 'block',
                              marginBottom: '0.5rem',
                              fontWeight: '500',
                              color: '#333'
                            }}>
                              Description
                            </label>
                            <textarea
                              value={newInternship.description}
                              onChange={(e) => handleInputChange('description', e.target.value)}
                              placeholder="Brief description of the internship role and responsibilities..."
                              rows={3}
                              style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '2px solid #e9ecef',
                                borderRadius: '8px',
                                fontSize: '0.95rem',
                                resize: 'vertical'
                              }}
                            />
                          </div>
                        </div>

                        {/* Form Actions */}
                        <div style={{
                          display: 'flex',
                          gap: '1rem',
                          justifyContent: 'flex-end',
                          paddingTop: '1rem',
                          borderTop: '1px solid #e9ecef'
                        }}>
                          <button
                            type="button"
                            onClick={() => setShowAssignModal(false)}
                            disabled={assignLoading}
                            style={{
                              padding: '0.75rem 1.5rem',
                              background: '#6c757d',
                              color: '#ffffff',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontWeight: '500',
                              opacity: assignLoading ? 0.7 : 1
                            }}
                          >
                            Cancel
                          </button>

                          <button
                            type="submit"
                            disabled={assignLoading || getAvailableStudents().length === 0}
                            style={{
                              padding: '0.75rem 1.5rem',
                              background: getAvailableStudents().length === 0 ? '#6c757d' : '#17a2b8',
                              color: '#ffffff',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: getAvailableStudents().length === 0 ? 'not-allowed' : 'pointer',
                              fontWeight: '500',
                              opacity: assignLoading ? 0.7 : 1
                            }}
                          >
                            {assignLoading ? '⏳ Assigning...' : '➕ Assign Internship'}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              )}

              {/* Detail Modal */}
              {showDetailModal && selectedRecord && (
                <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
                  <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                    {/* Modal Header */}
                    <div style={{
                      background: '#238548',
                      color: '#ffffff',
                      padding: '1.5rem 2rem',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <h2 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 'bold' }}>
                        📋 Attendance Details
                      </h2>
                      <button
                        onClick={() => setShowDetailModal(false)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#ffffff',
                          fontSize: '1.5rem',
                          cursor: 'pointer',
                          padding: '0.25rem',
                          borderRadius: '4px',
                          lineHeight: 1
                        }}
                      >
                        ✕
                      </button>
                    </div>

                    {/* Modal Content */}
                    <div style={{ padding: '2rem' }}>
                      {/* Student & Internship Info */}
                      <div style={{
                        background: '#f8f9fa',
                        padding: '1.5rem',
                        borderRadius: '8px',
                        marginBottom: '1.5rem',
                        border: '1px solid #e9ecef'
                      }}>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                          gap: '1.5rem'
                        }}>
                          <div>
                            <h3 style={{ margin: '0 0 1rem 0', color: '#333' }}>Student Information</h3>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Name:</strong> {selectedRecord.student_name}
                            </div>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Student ID:</strong> {selectedRecord.student_id}
                            </div>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Email:</strong> {selectedRecord.student_email}
                            </div>
                          </div>

                          <div>
                            <h3 style={{ margin: '0 0 1rem 0', color: '#333' }}>Internship Details</h3>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Company:</strong> {selectedRecord.company_name}
                            </div>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Position:</strong> {selectedRecord.position}
                            </div>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Location:</strong> {selectedRecord.location_name}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Attendance Details */}
                      <div style={{
                        background: '#f8f9fa',
                        padding: '1.5rem',
                        borderRadius: '8px',
                        marginBottom: '1.5rem',
                        border: '1px solid #e9ecef'
                      }}>
                        <h3 style={{ margin: '0 0 1rem 0', color: '#333' }}>Attendance Details</h3>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                          gap: '1rem'
                        }}>
                          <div>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Check-in Time:</strong> {formatDate(selectedRecord.check_in_time)} at {formatTime(selectedRecord.check_in_time)}
                            </div>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Status:</strong>
                              <span style={{
                                marginLeft: '0.5rem',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '12px',
                                fontSize: '0.8rem',
                                fontWeight: 'bold',
                                backgroundColor: getStatusInfo(selectedRecord.status).bg,
                                color: getStatusInfo(selectedRecord.status).color
                              }}>
                                {getStatusInfo(selectedRecord.status).icon} {getStatusInfo(selectedRecord.status).text}
                              </span>
                            </div>
                          </div>
                          <div>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Coordinates:</strong> {selectedRecord.latitude?.toFixed(6)}, {selectedRecord.longitude?.toFixed(6)}
                            </div>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Submitted:</strong> {formatDate(selectedRecord.created_at)}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Photo Preview */}
                      <div style={{
                        background: '#f8f9fa',
                        padding: '1.5rem',
                        borderRadius: '8px',
                        marginBottom: '1.5rem',
                        border: '1px solid #e9ecef',
                        textAlign: 'center'
                      }}>
                        <h3 style={{ margin: '0 0 1rem 0', color: '#333' }}>Attendance Photo</h3>
                        <img
                          src={`${API_LINK}${selectedRecord.photo_url}`}
                          alt="Attendance Photo"
                          style={{
                            maxWidth: '300px',
                            maxHeight: '200px',
                            objectFit: 'cover',
                            borderRadius: '8px',
                            border: '2px solid #ddd',
                            cursor: 'pointer'
                          }}
                          onClick={() => {
                            setSelectedPhoto(`${API_LINK}${selectedRecord.photo_url}`);
                            setShowPhotoModal(true);
                          }}
                        />
                        <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.8rem', color: '#666' }}>
                          Click to view full size
                        </p>
                      </div>

                      {/* Review Section */}
                      {selectedRecord.status === 'pending' && (
                        <div style={{
                          background: '#fff3cd',
                          padding: '1.5rem',
                          borderRadius: '8px',
                          marginBottom: '1.5rem',
                          border: '1px solid #ffeaa7'
                        }}>
                          <h3 style={{ margin: '0 0 1rem 0', color: '#333' }}>Review Attendance</h3>
                          <textarea
                            value={reviewNotes}
                            onChange={(e) => setReviewNotes(e.target.value)}
                            placeholder="Add review notes (optional)..."
                            rows={3}
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              border: '2px solid #e9ecef',
                              borderRadius: '8px',
                              fontSize: '0.95rem',
                              resize: 'vertical',
                              marginBottom: '1rem'
                            }}
                          />
                          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                            <button
                              onClick={() => handleAttendanceAction(selectedRecord.id, 'approved', reviewNotes, hodData?.name)}
                              disabled={actionLoading}
                              style={{
                                padding: '0.75rem 1.5rem',
                                background: '#28a745',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontWeight: '500',
                                opacity: actionLoading ? 0.7 : 1
                              }}
                            >
                              {actionLoading ? '⏳ Processing...' : '✅ Approve'}
                            </button>

                            <button
                              onClick={() => handleAttendanceAction(selectedRecord.id, 'rejected', reviewNotes, hodData?.name)}
                              disabled={actionLoading}
                              style={{
                                padding: '0.75rem 1.5rem',
                                background: '#dc3545',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontWeight: '500',
                                opacity: actionLoading ? 0.7 : 1
                              }}
                            >
                              {actionLoading ? '⏳ Processing...' : '❌ Reject'}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Existing Review */}
                      {selectedRecord.review_notes && (
                        <div style={{
                          background: selectedRecord.status === 'approved' ? '#d4edda' : '#f8d7da',
                          padding: '1.5rem',
                          borderRadius: '8px',
                          border: `1px solid ${selectedRecord.status === 'approved' ? '#c3e6cb' : '#f5c6cb'}`
                        }}>
                          <h3 style={{ margin: '0 0 1rem 0', color: '#333' }}>Review History</h3>
                          <div style={{ marginBottom: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
                            Reviewed by <strong>{selectedRecord.reviewed_by}</strong> on {formatDate(selectedRecord.reviewed_at)}
                          </div>
                          <p style={{ margin: 0, fontSize: '1rem', color: '#333' }}>
                            {selectedRecord.review_notes}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Photo Modal */}
              {showPhotoModal && selectedPhoto && (
                <div className="modal-overlay" onClick={() => setShowPhotoModal(false)}>
                  <div className="photo-modal" onClick={(e) => e.stopPropagation()}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '1rem',
                      width: '100%'
                    }}>
                      <h3 style={{ margin: 0, color: '#333' }}>Attendance Photo</h3>
                      <button
                        onClick={() => setShowPhotoModal(false)}
                        style={{
                          background: '#dc3545',
                          border: 'none',
                          color: '#ffffff',
                          fontSize: '1.2rem',
                          cursor: 'pointer',
                          padding: '0.5rem',
                          borderRadius: '4px',
                          lineHeight: 1
                        }}
                      >
                        ✕
                      </button>
                    </div>
                    <img
                      src={selectedPhoto}
                      alt="Attendance Photo"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '70vh',
                        objectFit: 'contain',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
                      }}
                    />
                  </div>
                </div>
              )}

            </>
          )}
        </>
      )}
    </Layout>
  );
};

export default HodInternshipAttendance;